import {
	previewTargetingFiltersSelector,
	targetingFiltersSelector,
} from '@/redux/store/slices/grid/gridSlice'
import { selectedCellsSelector } from '@/redux/store/slices/map/mapSlice'
import { useAppSelector } from '@redux/hooks/useAppState'
import { h3SetToFeature } from 'geojson2h3'
import { useMemo } from 'react'

import type { AggregatedDemographics } from 'honeygrid-types'

interface UseTargetingPreviewArgs {
	selectedReferenceField: string | null
	aggregatedDemographics?: AggregatedDemographics
}

export const useTargetingPreview = ({
	selectedReferenceField,
	aggregatedDemographics,
}: UseTargetingPreviewArgs) => {
	const tableSelection = useAppSelector(selectedCellsSelector)
	const draftTargetingFilters = useAppSelector(targetingFiltersSelector)
	const previewTargetingFilters = useAppSelector(previewTargetingFiltersSelector)

	// Calculate targeting preview cells based on which filters are marked as being previewed
	const targetingPreview = useMemo(() => {
		if (
			selectedReferenceField === null ||
			!previewTargetingFilters[selectedReferenceField] ||
			!draftTargetingFilters?.[selectedReferenceField] ||
			!aggregatedDemographics?.sortedCellIds?.[selectedReferenceField]
		) {
			return []
		}

		const draftPercentage = draftTargetingFilters[selectedReferenceField]?.percentage ?? 0

		// We store percentage as a whole number to avoid floating point errors
		// So we need to divide the percentage by 100 to get the actual percentage
		const list = aggregatedDemographics.sortedCellIds?.[selectedReferenceField] ?? []
		const selectionIndex = Math.floor((draftPercentage * list.length) / 100)

		return list.slice(0, selectionIndex)
	}, [
		aggregatedDemographics?.sortedCellIds,
		selectedReferenceField,
		draftTargetingFilters,
		previewTargetingFilters,
	])

	// Combine targeting preview with table selection and convert to GeoJSON
	const targetingPreviewGeoJson = useMemo(() => {
		// Always include table selection, even if there's no targeting preview
		const combinedSelection = [...new Set([...tableSelection, ...targetingPreview])] as string[]

		if (!combinedSelection.length) {
			return null
		}

		return h3SetToFeature(combinedSelection)
	}, [tableSelection, targetingPreview])

	return targetingPreviewGeoJson
}
